import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useEventCallback, usePrevious } from '@gitbook/util-react-hooks';

import { getPaletteId } from './id';
import { openedPaletteAtom, paletteOpenedByIdSelector } from './state';
import { createPalette, executeCommandInPalette, typeQueryInPalette } from './transforms';
import type { PaletteAnchorRef, PaletteCommandDef, PaletteDef } from './types';

/**
 * A command palette handle is an instance of the palette that can be used
 * in specific components.
 */
export interface PaletteHandle {
    /**
     * Whether the palette associated with the anchor is opened or not.
     */
    opened: boolean;

    /**
     * Open the commands palette.
     * If no palette is provided, the default palette will open.
     */
    open: (palette: PaletteDef) => void;

    /**
     * Execute a command.
     */
    executeCommand: (command: PaletteCommandDef) => Promise<void>;

    /**
     * Close the commands palette.
     */
    close: () => void;

    /**
     * Update the search query in the palette.
     */
    typeQuery: (query: string) => void;
}

/**
 * Hook to create a palette handle.
 * When providing an anchor, the handle can be used to open a specific commands palette next to it.
 */
export function usePaletteHandle(
    anchorRef: PaletteAnchorRef,
    options: {
        /** Callback when the palette is closed */
        onClose?: () => void;
    } = {}
): PaletteHandle {
    const { onClose } = options;

    // Create a reference so that the palette object ref doesn't change
    const handleRef = React.useRef<PaletteHandle>({} as PaletteHandle);
    const notifyClose = useEventCallback(() => onClose?.());

    const id = getPaletteId(anchorRef);
    const opened = useRecoilValue(paletteOpenedByIdSelector(id));
    const setPalette = useSetRecoilState(openedPaletteAtom);

    /*
     * Trigger onClose when the palette is closed
     */
    const prevOpened = usePrevious(opened);
    React.useEffect(() => {
        if (!opened && prevOpened) {
            notifyClose();
        }
    }, [opened, prevOpened, notifyClose]);

    /*
     * When it's unmounted, close the palette if it has been opened by this handle.
     */
    React.useEffect(() => {
        return () => {
            setPalette((prev) => {
                if (prev && prev.id === id) {
                    return null;
                }
                return prev;
            });
        };
    }, [setPalette, id]);

    /*
     * Create the API
     */

    handleRef.current.opened = opened;

    handleRef.current.open = useEventCallback((palette: PaletteDef) => {
        setPalette((prev) => {
            if (prev && prev.palette === palette && prev.id === id) {
                return prev;
            }
            return { palette, id, anchorRef };
        });
    });

    handleRef.current.close = useEventCallback(() => {
        setPalette(null);
    });

    handleRef.current.typeQuery = useEventCallback((query: string) => {
        setPalette((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                palette: typeQueryInPalette(prev.palette, query),
            };
        });
    });

    handleRef.current.executeCommand = useEventCallback(async (command: PaletteCommandDef) => {
        const palette = await executeCommandInPalette(createPalette({}), command);
        setPalette(() => {
            return palette ? { palette, id, anchorRef } : null;
        });
    });

    return handleRef.current;
}
